/**
 * Texto de un PDF, leído en el navegador.
 *
 * Lo comparten los dos documentos de Salud SAT —la opinión de cumplimiento y la
 * constancia de situación fiscal— porque el arranque del worker de pdf.js y el
 * respaldo de OCR son los mismos para ambos. Duplicarlos dejaría dos sitios que
 * arreglar el día que cambie la ruta del worker, y uno se olvidaría.
 *
 * Lo que NO vive aquí es qué significa el texto: eso es de cada documento, y
 * cada uno tiene su propio módulo porque sus estructuras no coinciden.
 */

/** Texto útil suficiente para no recurrir al OCR. */
const HAY_CAPA_DE_TEXTO = 200;

/**
 * Normaliza para comparar: mayúsculas, sin acentos y con los espacios
 * colapsados. El OCR devuelve acentos irregulares y saltos donde el texto
 * nativo no los tiene.
 */
export const normalizar = (texto) =>
  (texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();

const conPdfJs = async (buffer) => {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
  const worker = await import('pdfjs-dist/legacy/build/pdf.worker.entry');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default || worker;

  const documento = await pdfjs.getDocument({ data: buffer }).promise;
  const partes = [];

  for (let i = 1; i <= documento.numPages; i += 1) {
    const pagina = await documento.getPage(i);
    const contenido = await pagina.getTextContent();
    partes.push(contenido.items.map((it) => it.str).join(' '));
  }

  return { documento, texto: partes.join(' ') };
};

/**
 * OCR de respaldo: solo para documentos SIN capa de texto —un escaneo, la foto
 * de una impresión—. Va en import dinámico porque el motor y sus datos de
 * idioma pesan mucho más que todo lo demás, y el camino normal no los necesita.
 */
const conOcr = async (documento) => {
  const { default: Tesseract } = await import('tesseract.js');

  const pagina = await documento.getPage(1);
  const viewport = pagina.getViewport({ scale: 2 });

  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await pagina.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;

  const { data } = await Tesseract.recognize(canvas, 'spa');
  return data.text || '';
};

/**
 * Extrae el texto de un PDF: capa de texto primero, OCR solo si no la hay.
 *
 * @param {Blob} blob
 * @returns {Promise<{texto: string, origen: 'texto'|'ocr'}>}
 */
export const extraerTexto = async (blob) => {
  const buffer = new Uint8Array(await blob.arrayBuffer());
  const { documento, texto } = await conPdfJs(buffer);

  if (normalizar(texto).length >= HAY_CAPA_DE_TEXTO) {
    return { texto, origen: 'texto' };
  }

  return { texto: await conOcr(documento), origen: 'ocr' };
};

/**
 * Antigüedad en palabras. Lo que el usuario tiene que juzgar es "¿esto sigue
 * valiendo?", no "¿qué día fue?".
 */
export const antiguedad = (fecha, ahora = new Date()) => {
  if (!fecha) return null;

  const dias = Math.floor((ahora - fecha) / 86400000);

  if (dias < 0) return null;
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'hace 1 día';
  if (dias < 30) return `hace ${dias} días`;

  const meses = Math.floor(dias / 30);
  if (meses === 1) return 'hace 1 mes';
  if (meses < 12) return `hace ${meses} meses`;

  const años = Math.floor(dias / 365);
  return años === 1 ? 'hace 1 año' : `hace ${años} años`;
};

/** Umbral único para los dos documentos de Salud SAT. */
export const DIAS_VIGENCIA = 30;

/**
 * ¿El documento tiene más de un mes?
 *
 * 30 días y no "mes natural": el mes natural obliga a decidir qué pasa con un
 * documento emitido el 31 —¿vence el 28 de febrero, el 2 de marzo?— y produce
 * discusiones sobre un umbral que es orientativo.
 *
 * Sin fecha devuelve `false`: no se advierte de antigüedad, pero tampoco se
 * afirma que el documento sea reciente. Eso lo distingue quien llama, mirando
 * si hay fecha.
 */
export const tieneMasDeUnMes = (fecha, ahora = new Date()) => {
  if (!fecha) return false;
  return Math.floor((ahora - fecha) / 86400000) > DIAS_VIGENCIA;
};
